import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import {
    apiGetIndustryCodes,
    apiGetIndustryKeyFigureAggregations
} from 'api/industries';
import {
    AggregationKeyFigureResponse,
    Industry,
    IndustryInfo,
    IndustryResponse,
    KeyFigureData
} from 'components/context/types';
import { Cascader } from 'antd';
import styled from 'styled-components';
import t from 'utils/translations';
import { CompanyKeyFigureGraphs } from 'components/common/KeyFigureGraph/CompanyKeyFigureGraphs';

import { CascaderOptionType } from 'antd/es/cascader';
import Loading from 'components/common/Loading';
import { prepareAggregationsToKeyFigureData } from 'utils/keyfigures';
import { getTitleElement } from 'components/pages/frontpage/common';

const IndustryCascader = styled(Cascader)`
    width: 100%;
`;

const cascaderResultsHeight = 350;

const IndustryCascaderContainer = styled.div`
    position: relative;
    height: ${cascaderResultsHeight + 60 + 'px'};

    .ant-cascader-menus {
        width: 100%;
        border-radius: 3px;
        box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e5e5;
        z-index: 999; // this makes sure that the cascader stays behind modals
    }

    .ant-cascader-menu {
        width: 20%;
        height: ${cascaderResultsHeight + 'px'};
        overflow-x: hidden;
    }

    .ant-cascader-menu-item {
        white-space: normal;
        padding: 7px 5px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .ant-cascader-menu-item-expand-icon {
        display: none !important;
    }

    .ant-cascader-picker-label {
        direction: rtl;
        text-align: left;
        padding-right: 30px;
    }
    .ant-cascader-picker-arrow {
        display: none;
    }
`;

export default function IndustryTab() {
    const [industryTree, setIndustryTree] = useState<Industry[] | undefined>(
        undefined
    );

    const [selectedIndustry, setSelectedIndustry] = useState<
        IndustryInfo | undefined
    >({ code: '*', label: 'KAIKKI YRITYKSET' });

    const [industryKeyFigureData, setIndustryKeyFigureData] = useState<
        KeyFigureData | undefined
    >(undefined);

    const [isError, setIsError] = useState(false);

    const isLoading = industryTree === undefined;

    const industryCascaderContainer = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!industryTree) {
            apiGetIndustryCodes()
                .then(res => res.json())
                .then((data: IndustryResponse) => {
                    setIndustryTree(data.content);
                })
                .catch(() => setIsError(true));
        }
        if (selectedIndustry) {
            apiGetIndustryKeyFigureAggregations(selectedIndustry.code)
                .then(res => res.json())
                .then((data: AggregationKeyFigureResponse) => {
                    setIndustryKeyFigureData(
                        prepareAggregationsToKeyFigureData(
                            data.content,
                            selectedIndustry.label
                        )
                    );
                })
                .catch(() => setIsError(true));
        }
    }, [industryTree, selectedIndustry]);

    const changeIndustry = (
        value: string[],
        selectedOptions: CascaderOptionType[]
    ) => {
        if (selectedOptions.length > 0) {
            const option = selectedOptions[selectedOptions.length - 1];
            const code: string = option.value as string;
            const label: string = option.label as string;
            setSelectedIndustry({ code, label });
        } else {
            setSelectedIndustry(undefined);
            setIndustryKeyFigureData(undefined);
        }
    };

    return (
        <>
            <IndustryCascaderContainer ref={industryCascaderContainer}>
                <IndustryCascader
                    changeOnSelect={true}
                    defaultValue={['*']}
                    disabled={isLoading}
                    getPopupContainer={() =>
                        industryCascaderContainer.current
                            ? industryCascaderContainer.current
                            : document.body
                    }
                    onChange={changeIndustry}
                    options={industryTree}
                    popupVisible={true}
                    placeholder={t('CHOOSE_INDUSTRY')}
                    showSearch={true}
                />
                {isLoading && <Loading height={350} />}
            </IndustryCascaderContainer>

            <>
                <CompanyKeyFigureGraphs
                    isAggregateData={true}
                    showMonths={false}
                    canCompare={true}
                    isLoading={false}
                    keyFigureData={industryKeyFigureData}
                    title={
                        selectedIndustry
                            ? getTitleElement(
                                  `${t('INDUSTRY_KF_TITLE')} \u2014 ${
                                      selectedIndustry.label
                                  }`,
                                  t('GRAPH_INFO_LARGE_SAMPLES')
                              )
                            : getTitleElement(
                                  t('INDUSTRY_KF_TITLE'),
                                  t('GRAPH_INFO_LARGE_SAMPLES')
                              )
                    }
                />
            </>
        </>
    );
}
